import {
  AnimatedCommonIcon,
  liquidGlassPressTransition,
  liquidGlassSurfaceFillSx,
  LIQUID_GLASS_PRESS_ORIGIN,
  LIQUID_GLASS_PRESS_SCALE,
} from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Caption } from '@telegram-apps/telegram-ui';
import { motion } from 'framer-motion';
import type { FC } from 'react';
import { FLOATING_TABBAR_BAR_HEIGHT } from '../../helpers/floatingTabbarLayout';
import { TABBAR_INDICATOR_INSET_Y } from './constants';
import { floatingTabPanelId, type FloatingTab } from './types';
import type { useFloatingTabStrip } from './useFloatingTabStrip';

type FloatingTabStripProps = {
  tabs: FloatingTab[];
  selectedId: string;
  strip: ReturnType<typeof useFloatingTabStrip>;
};

const iconColor = (selected: boolean): string =>
  selected ? 'var(--tg-theme-button-color)' : 'var(--tg-theme-hint-color)';

export const FloatingTabStrip: FC<FloatingTabStripProps> = ({ tabs, selectedId, strip }) => {
  const {
    stripRef,
    isPressed,
    displayIndex,
    focusedIndex,
    indicatorLayout,
    indicatorMotionReady,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onKeyDown,
  } = strip;

  return (
    <Box
      component={motion.div}
      initial={false}
      animate={{ scale: isPressed ? LIQUID_GLASS_PRESS_SCALE : 1 }}
      transition={liquidGlassPressTransition}
      sx={{
        flex: 1,
        minWidth: 0,
        transformOrigin: LIQUID_GLASS_PRESS_ORIGIN,
      }}
    >
      <Box
        sx={{
          height: FLOATING_TABBAR_BAR_HEIGHT,
          borderRadius: '999px',
          overflow: 'visible',
        }}
      >
        <Box
          ref={stripRef}
          role="tablist"
          tabIndex={0}
          aria-activedescendant={floatingTabPanelId(tabs[focusedIndex]?.id ?? selectedId)}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          onKeyDown={onKeyDown}
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            borderRadius: 'inherit',
            display: 'flex',
            alignItems: 'stretch',
            overflow: 'hidden',
            pointerEvents: 'auto',
            touchAction: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            cursor: 'pointer',
            outline: 'none',
            ...liquidGlassSurfaceFillSx,
            '&:focus-visible [aria-selected="true"]': {
              outline: '2px solid var(--tg-theme-link-color)',
              outlineOffset: -2,
              borderRadius: '999px',
            },
          }}
        >
          {indicatorLayout ? (
            <Box
              component={motion.div}
              aria-hidden
              initial={false}
              animate={{ x: indicatorLayout.x, width: indicatorLayout.width }}
              transition={
                isPressed
                  ? { type: 'spring', stiffness: 900, damping: 48, mass: 0.35 }
                  : indicatorMotionReady
                  ? { type: 'spring', stiffness: 520, damping: 38, mass: 0.55 }
                  : { duration: 0 }
              }
              sx={{
                position: 'absolute',
                top: TABBAR_INDICATOR_INSET_Y,
                left: 0,
                height: indicatorLayout.height,
                borderRadius: '999px',
                bgcolor: 'color-mix(in srgb, var(--tg-theme-text-color) 8%, transparent)',
                pointerEvents: 'none',
              }}
            />
          ) : null}
          {tabs.map(({ id, text, icon }, index) => {
            const selected = displayIndex === index;
            const color = iconColor(selected);
            return (
              <Box
                key={id}
                id={floatingTabPanelId(id)}
                role="tab"
                aria-label={text}
                aria-selected={selectedId === id}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  flex: '1 1 0',
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  px: 0.25,
                }}
              >
                <Stack alignItems="center" justifyContent="center" spacing={0.4} sx={{ minWidth: 0 }}>
                  <AnimatedCommonIcon size={24} icon={icon} type="outlined" isSelected={selected} color={color} />
                  <Caption
                    style={{
                      fontSize: 7,
                      fontWeight: selected ? 600 : 500,
                      lineHeight: 1,
                      letterSpacing: '-0.1px',
                      color,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {text}
                  </Caption>
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};
